import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import {
  faCheckCircle,
  faExclamationTriangle,
  faFilter,
  faInfoCircle,
  faSyncAlt,
  faTimes,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import {
  coolGrey,
  darkerSalmon,
  greyStroke,
  modernSalmon,
  modernSalmonBackground,
  shadowLight,
  shadowMedium,
  softBlue,
  softBlueDark,
  softBlueLight,
  softGreen,
  softGreenDark,
  softGreenLight,
  squidInk,
  white,
} from 'lib/css/colors';
import pointerOnHover from 'lib/css/pointerOnHover';

export const TILE_FILTERS = {
  EXECUTED: 'EXECUTED',
  FAILED: 'FAILED',
  FULLY_FAILED: 'FULLY_FAILED',
  RUNNING: 'RUNNING',
};

const TILE_CONFIGS = {
  [TILE_FILTERS.EXECUTED]: {
    label: 'Recurring executed',
    tooltipText: 'Distinct recurring, non-archived sources with at least one run in the window.',
    icon: faCheckCircle,
    gradient: [softGreen, softGreenDark],
    iconBg: softGreenLight,
    iconColor: softGreenDark,
  },
  [TILE_FILTERS.FAILED]: {
    label: 'Failed runs',
    tooltipText: 'Total number of failed runs across all recurring sources in the window.',
    icon: faExclamationTriangle,
    gradient: [modernSalmon, darkerSalmon],
    iconBg: modernSalmonBackground,
    iconColor: darkerSalmon,
  },
  [TILE_FILTERS.FULLY_FAILED]: {
    label: 'Fully-failed sources',
    tooltipText: 'Recurring sources where every run in the window failed.',
    icon: faTimesCircle,
    gradient: [modernSalmon, darkerSalmon],
    iconBg: modernSalmonBackground,
    iconColor: darkerSalmon,
  },
  [TILE_FILTERS.RUNNING]: {
    label: 'Currently running',
    tooltipText: 'Recurring sources that currently have an in-progress run.',
    icon: faSyncAlt,
    gradient: [softBlue, softBlueDark],
    iconBg: softBlueLight,
    iconColor: softBlueDark,
  },
};

function formatCount(count) {
  return (count ?? 0).toLocaleString('en-US');
}

function SummaryTile({ filterKey, count, isActive, onClick }) {
  const config = TILE_CONFIGS[filterKey];

  return (
    <div
      className={classNames(css(styles.tile, isActive && styles.tileActive), pointerOnHover)}
      onClick={onClick}
    >
      <div
        className={css(styles.accentBar)}
        style={{
          background: `linear-gradient(90deg, ${config.gradient[0]}, ${config.gradient[1]})`,
        }}
      />
      <div className={css(styles.tileContent)}>
        <div className={css(styles.tileHeader)}>
          <div className={css(styles.iconContainer)} style={{ backgroundColor: config.iconBg }}>
            <FontAwesomeIcon
              icon={config.icon}
              className={css(styles.icon)}
              style={{ color: config.iconColor }}
            />
          </div>
          <div className={css(styles.titleRow)}>
            <span className={css(styles.title)}>{config.label}</span>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-${filterKey}`}>{config.tooltipText}</Tooltip>}
            >
              <span className={css(styles.tooltipIcon)}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </OverlayTrigger>
          </div>
        </div>
        <div className={css(styles.tileValue)}>{formatCount(count)}</div>
        <div className={css(styles.tileFooter)}>
          <FontAwesomeIcon
            icon={isActive ? faTimes : faFilter}
            className={css(styles.footerIcon)}
          />
          {isActive ? 'Clear filter' : 'Click to filter'}
        </div>
      </div>
    </div>
  );
}

SummaryTile.propTypes = {
  filterKey: PropTypes.oneOf(Object.values(TILE_FILTERS)).isRequired,
  count: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    boxShadow: `0 1px 3px ${shadowLight}, 0 1px 2px ${shadowLight}`,
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: `0 4px 12px ${shadowMedium}`,
      transform: 'translateY(-2px)',
    },
  },
  tileActive: {
    boxShadow: `0 0 0 2px ${softBlue}`,
  },
  accentBar: {
    height: 4,
    width: '100%',
  },
  tileContent: {
    padding: '16px 20px 14px',
  },
  tileHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: 18,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
    lineHeight: 1.4,
  },
  tooltipIcon: {
    color: coolGrey,
    opacity: 0.6,
    cursor: 'pointer',
    fontSize: 12,
    ':hover': {
      opacity: 1,
    },
  },
  tileValue: {
    fontSize: 28,
    fontWeight: 700,
    color: squidInk,
    lineHeight: 1.2,
    marginBottom: 12,
  },
  tileFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: coolGrey,
    paddingTop: 12,
    borderTop: `1px solid ${greyStroke}`,
  },
  footerIcon: {
    fontSize: 11,
  },
});

export default SummaryTile;
